import { useLocation } from 'react-router-dom'

import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'
import { useScheduleLastView } from '~publish/hooks/useScheduleLastView'
import {
  channelSchedule,
  newCalendarSingleChannel,
} from '~publish/legacy/routes'

import { filterQueryParamsByRoute } from './utils/filterQueryParamsByRoute'

/**
 * Build channel schedule URL with preserved filter params (tags, tab, etc.)
 *
 * This hook ensures that when navigating between channels in the new IA,
 * the selected filters (like tags) persist in the URL.
 *
 * It also respects the user's view preference - if they prefer calendar view,
 * it returns the calendar URL instead of the list URL.
 *
 * @param channelId - The ID of the channel to navigate to
 * @returns URL string with preserved query parameters
 *
 * @example
 * ```tsx
 * const channelUrl = useChannelUrl(channel.id)
 * // If user prefers calendar: /channels/123/calendar/week?tags[]=abc
 * // If user prefers list: /channels/123/schedule?tags[]=abc
 * ```
 */
export function useChannelUrl(channelId: string): string {
  const location = useLocation()
  const { prefersCalendar, calendarGranularity } = useScheduleLastView()
  const currentSearch = parseQueryParams(location.search)

  if (prefersCalendar) {
    // Filter current search params to only include those valid for calendar routes
    const filteredParams = filterQueryParamsByRoute(
      currentSearch,
      newCalendarSingleChannel.queryParams,
    )

    const queryString = serializeQueryParams(filteredParams)
    return (
      newCalendarSingleChannel.buildPathname({
        id: channelId,
        granularity: calendarGranularity,
      }) + (queryString ? `?${queryString}` : '')
    )
  }

  // Filter current search params to only include those valid for channel schedule routes
  const filteredParams = filterQueryParamsByRoute(
    currentSearch,
    channelSchedule.queryParams,
  )

  const queryString = serializeQueryParams(filteredParams)
  return (
    channelSchedule.buildPathname({ id: channelId }) +
    (queryString ? `?${queryString}` : '')
  )
}
